import { createFileRoute, Link } from "@tanstack/react-router";
import type { ComponentType } from "react";
import { BookOpen, RefreshCcw, Users, MapPin, BookHeart, Target, Heart, Gift, Search } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { useI18n } from "@/lib/i18n";
import { pageHead, jsonLd, SITE_NAME, SITE_URL } from "@/lib/seo";
import heroImg from "@/assets/hero-books.jpg";
import heroDarkImg from "@/assets/hero-books-dark.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    ...pageHead({
      path: "/",
      title: "Book Swap — Borrow Books From Your Neighbors",
      description: "Lend, borrow, swap and gift books with people on your street. Free, local and community-powered — no shipping, no fees.",
    }),
    scripts: [
      jsonLd({
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/browse?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      }),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
      }),
    ],
  }),

  component: Index,
});

function Index() {
  const { t } = useI18n();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-cozy)" }} />
        <div className="pointer-events-none absolute -top-20 right-10 -z-10 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-10 -left-10 -z-10 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
        <div className="mx-auto grid max-w-6xl gap-12 px-4 py-16 md:py-24 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-card/70 px-3 py-1 text-xs font-semibold text-primary backdrop-blur">
              <MapPin className="h-3 w-3" /> {t("home.badge")}
            </span>
            <h1 className="mt-5 font-serif text-5xl leading-tight md:text-6xl">
              {t("home.h1.a")} <span className="italic text-primary">{t("home.h1.b")}</span>
            </h1>
            <p className="mt-5 max-w-lg font-serif text-lg italic text-muted-foreground">
              {t("home.sub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <>
                  <Link to="/browse"><Button size="lg" className="bg-primary hover:bg-primary/90 shadow-[var(--shadow-elegant)]">{t("home.cta_browse")}</Button></Link>
                  <Link to="/my-books"><Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary-soft">{t("nav.my_books")}</Button></Link>
                </>
              ) : (
                <>
                  <Link to="/register"><Button size="lg" className="bg-primary hover:bg-primary/90 shadow-[var(--shadow-elegant)]">{t("home.cta_start")}</Button></Link>
                  <Link to="/browse"><Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary-soft">{t("home.cta_browse")}</Button></Link>
                </>
              )}
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              <FeatureHighlight icon={Search} label="Discover local books" />
              <FeatureHighlight icon={Users} label="Meet fellow readers" />
              <FeatureHighlight icon={Heart} label="Free forever" />
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-primary/10 blur-2xl dark:bg-primary/5" />
            <img src={heroImg} alt="A colorful stack of books with a purple bookmark"
              width={1280} height={960} fetchPriority="high" decoding="async"
              className="relative animate-float rounded-3xl shadow-[var(--shadow-book)] block dark:hidden" />
            <img src={heroDarkImg} alt="A stack of books in a moody dark setting"
              width={1280} height={960} fetchPriority="high" decoding="async"
              className="relative animate-float rounded-3xl shadow-[var(--shadow-book)] hidden dark:block" />
          </div>
        </div>
      </section>

      {/* Intro story */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h2 className="font-serif text-3xl md:text-4xl">{t("home.story.title")}</h2>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{t("home.story.p1")}</p>
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{t("home.story.p2")}</p>
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{t("home.story.p3")}</p>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <h2 className="text-center text-3xl font-bold md:text-4xl">{t("home.how.title")}</h2>
        <p className="mt-3 text-center text-muted-foreground">{t("home.how.sub")}</p>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            { icon: BookOpen, title: t("home.how.list_t"), text: t("home.how.list_d") },
            { icon: Search, title: t("home.how.browse_t"), text: t("home.how.browse_d") },
            { icon: RefreshCcw, title: t("home.how.swap_t"), text: t("home.how.swap_d") },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl bg-card p-6 shadow-[var(--shadow-card)] transition-transform hover:-translate-y-1">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary-soft text-primary"><f.icon className="h-6 w-6" /></div>
              <h3 className="mt-4 text-xl font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature grid */}
      <section className="mx-auto max-w-6xl px-4 pb-16">
        <h2 className="text-center text-3xl font-bold md:text-4xl">{t("home.more.title")}</h2>
        <p className="mt-3 text-center text-muted-foreground">{t("home.more.sub")}</p>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Feat icon={BookOpen} title={t("home.feat.qr_t")} text={t("home.feat.qr_d")} to="/journey" />
          <Feat icon={Users} title={t("home.feat.clubs_t")} text={t("home.feat.clubs_d")} to="/clubs" />
          <Feat icon={Heart} title={t("home.feat.donate_t")} text={t("home.feat.donate_d")} to="/browse" />
          <Feat icon={BookHeart} title={t("home.feat.recs_t")} text={t("home.feat.recs_d")} to="/recommendations" />
          <Feat icon={Target} title={t("home.feat.badges_t")} text={t("home.feat.badges_d")} to="/missions" />
          <Feat icon={BookOpen} title={t("home.feat.family_t")} text={t("home.feat.family_d")} to="/shelves" />
        </div>
      </section>

      {/* CTA — signed-out only */}
      {!user && (
        <section className="px-4 pb-20">
          <div className="mx-auto max-w-5xl rounded-3xl px-8 py-14 text-center text-white shadow-[var(--shadow-elegant)]" style={{ background: "var(--gradient-hero)" }}>
            <Gift className="mx-auto h-10 w-10 opacity-80" />
            <h2 className="mt-4 text-3xl font-bold text-white md:text-4xl">{t("home.cta.title")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-white/80">{t("home.cta.sub")}</p>
            <Link to="/register" className="mt-7 inline-block">
              <Button size="lg" className="bg-white text-primary-dark hover:bg-white/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90">{t("home.cta.btn")}</Button>
            </Link>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}


function Feat({ icon: Icon, title, text, to }: { icon: ComponentType<{ className?: string }>; title: string; text: string; to: string }) {
  return (
    <Link to={to} className="group block rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[var(--shadow-card)]">
      <Icon className="h-6 w-6 text-primary" />
      <h3 className="mt-3 font-semibold group-hover:text-primary">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </Link>
  );
}

function FeatureHighlight({ icon: Icon, label }: { icon: ComponentType<{ className?: string }>; label: string }) {
  return (
    <div className="rounded-xl bg-card/60 p-3 text-center backdrop-blur">
      <Icon className="mx-auto h-6 w-6 text-primary" aria-hidden="true" />
      <div className="mt-1 text-xs font-semibold text-card-foreground">{label}</div>
    </div>
  );
}
